import { useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { queryClient, trpc } from '@/lib/trpc'
import { vendorLogoPath } from '@/lib/vendor-logo'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { PersonnelSyncStatus } from '@/components/compliance/people/PersonnelSyncStatus'
import type { ConfigModalProps, IntegrationProvider } from '../types'

function SuccessFactorsConfigModal({
  open,
  onOpenChange,
  workspaceId,
  integration,
}: ConfigModalProps) {
  const { canManage, isLoading } = useWorkspacePermissions(workspaceId)
  const [connected, setConnected] = useState(!!integration)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const connect = useMutation({
    ...trpc.integrations.successfactors.connect.mutationOptions(),
    gcTime: 0,
  })
  const sync = useMutation(trpc.employee.syncPersonnel.mutationOptions())
  const status = useQuery({
    ...trpc.integrations.successfactors.connectionStatus.queryOptions(),
    enabled: open && connected,
    refetchInterval: open && connected ? 5000 : false,
  })
  const running =
    !!status.data?.jobState &&
    !['completed', 'failed', 'unknown'].includes(status.data.jobState)
  const busy = connect.isPending || sync.isPending
  const disabled = !canManage || isLoading || busy || running
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getConnectedProviderKeys.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.listProviderConnections.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.employee.listPersonnelProviders.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.successfactors.connectionStatus.queryKey(),
      }),
    ])
  const authorize = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (disabled) return
    const form = event.currentTarget
    const data = new FormData(form)
    const field = (name: string) => String(data.get(name) ?? '').trim()
    const input = {
      companyId: field('companyId'),
      apiServer: field('apiServer'),
      clientId: field('clientId'),
      userId: field('userId'),
      businessEmailType: field('businessEmailType'),
      certificate: field('certificate'),
      privateKey: field('privateKey'),
    }
    form.reset()
    setMessage('')
    setError('')
    try {
      await connect.mutateAsync(input)
      setConnected(true)
      setMessage('Connected. Sync personnel to collect the authorized roster.')
      await refresh()
    } catch {
      setError(
        'Could not connect SuccessFactors. Verify the API host, company, app registration, API user and matching signing credentials, then retry.'
      )
    } finally {
      connect.reset()
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next)
      }}
    >
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>SAP SuccessFactors personnel</DialogTitle>
          <DialogDescription>
            Sync authorized employment status and work email into People.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea
          type='scroll'
          className='max-h-[65vh]'
          viewClassName='max-h-[65vh] [&>div]:!block'
        >
          <div className='space-y-4 px-6 pb-4 text-sm'>
            {!canManage && !isLoading && (
              <p>Only workspace admins can connect or sync SuccessFactors.</p>
            )}
            <p className='text-muted-foreground'>
              A SuccessFactors administrator must register an OAuth client
              application with your signing certificate and authorize an API
              user to read the intended Employee Central population, employment
              records and Business Email. Signing credentials are used for token
              renewal.
            </p>
            <p className='text-muted-foreground'>
              Find the numeric Business EmailType Option ID in Admin Center →
              Picklist Center → ecEmailType → Business. Personal email is
              excluded. Missing people are never automatically treated as
              departures.
            </p>
            {connected && (
              <>
                {status.data && (
                  <p>
                    Company: {status.data.companyId} · API host:{' '}
                    {status.data.apiServer} · Business EmailType:{' '}
                    {status.data.businessEmailType}
                  </p>
                )}
                <PersonnelSyncStatus provider='successfactors' />
                {status.isError && (
                  <Button
                    variant='outline'
                    onClick={() => void status.refetch()}
                  >
                    Retry status
                  </Button>
                )}
                <Button
                  disabled={disabled || status.isPending || status.isError}
                  onClick={async () => {
                    setError('')
                    setMessage('')
                    try {
                      await sync.mutateAsync({ provider: 'successfactors' })
                      setMessage(
                        'Personnel sync queued. You can close this dialog while collection runs.'
                      )
                      await status.refetch()
                    } catch {
                      setError(
                        'Personnel sync did not start. Reload status, verify access and retry.'
                      )
                    }
                  }}
                >
                  {sync.isPending ? 'Starting…' : 'Sync personnel'}
                </Button>
                <Button variant='link' asChild>
                  <Link
                    to='/workspaces/$workspaceId/people'
                    params={{ workspaceId }}
                    onClick={() => onOpenChange(false)}
                  >
                    Open People
                  </Link>
                </Button>
                <p className='text-muted-foreground'>
                  This collection does not assess account roles, MFA or SSO.
                  Disconnect from the integration card before switching company
                  or API host. Existing evidence is retained; revoke the app
                  authorization in SuccessFactors separately.
                </p>
              </>
            )}
            <details open={!connected}>
              <summary className='cursor-pointer font-medium'>
                {connected
                  ? 'Reconnect or change Business EmailType'
                  : 'Connection setup'}
              </summary>
              <form
                className='mt-3 space-y-3'
                autoComplete='off'
                onSubmit={(event) => void authorize(event)}
              >
                <fieldset disabled={disabled} className='space-y-3'>
                  {(
                    [
                      ['companyId', 'Company ID'],
                      ['apiServer', 'API host (for example api41.sapsf.com)'],
                      ['clientId', 'OAuth API key'],
                      ['userId', 'API user ID'],
                      ['businessEmailType', 'Business EmailType Option ID'],
                    ] as const
                  ).map(([name, label]) => (
                    <div className='space-y-1' key={name}>
                      <Label htmlFor={`sap-${name}`}>{label}</Label>
                      <Input
                        id={`sap-${name}`}
                        name={name}
                        required
                        autoComplete='off'
                      />
                    </div>
                  ))}
                  <div className='space-y-1'>
                    <Label htmlFor='sap-certificate'>
                      Registered certificate (PEM)
                    </Label>
                    <Textarea
                      id='sap-certificate'
                      name='certificate'
                      required
                      autoComplete='off'
                      spellCheck={false}
                    />
                  </div>
                  <div className='space-y-1'>
                    <Label htmlFor='sap-privateKey'>
                      Matching private key (PEM)
                    </Label>
                    <Textarea
                      id='sap-privateKey'
                      name='privateKey'
                      required
                      autoComplete='off'
                      spellCheck={false}
                    />
                  </div>
                  <Button
                    type='submit'
                    variant={connected ? 'outline' : 'default'}
                  >
                    {connect.isPending
                      ? 'Connecting…'
                      : connected
                        ? 'Reconnect SuccessFactors'
                        : 'Connect SuccessFactors'}
                  </Button>
                </fieldset>
              </form>
            </details>
            {message && <p role='status'>{message}</p>}
            {error && (
              <p role='alert' className='text-destructive'>
                {error}
              </p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

export const SuccessFactorsProvider: IntegrationProvider = {
  id: 'SuccessFactors',
  name: 'SAP SuccessFactors',
  description: 'Authorized personnel and explicit employment departures',
  Icon: () => (
    <img
      src={vendorLogoPath('successfactors')}
      alt=''
      className='size-6 object-contain'
    />
  ),
  ConfigModal: (props) => (
    <SuccessFactorsConfigModal
      key={`${props.workspaceId}:${props.open}`}
      {...props}
    />
  ),
  getFeatures: () => [
    {
      id: 'personnel',
      name: 'Personnel directory',
      description:
        'Employment status, work email and explicit termination dates',
      enabled: true,
    },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    onSuccess?.({ requiresSetup: true })
    onFinish?.('success')
  },
  syncData: async () => {
    await queryClient
      .getMutationCache()
      .build(queryClient, trpc.employee.syncPersonnel.mutationOptions())
      .execute({ provider: 'successfactors' })
    await queryClient.invalidateQueries({
      queryKey: trpc.integrations.successfactors.connectionStatus.queryKey(),
    })
  },
}
